import React, { useState, useEffect } from "react";
import { IconButton as MaterialIconButton, FormControl, FormLabel, RadioGroup, Radio, FormControlLabel, TextField } from "@mui/material";
import { useNode, useEditor } from "@craftjs/core";
import { useTextPersistence } from "qwanyx";
import { useStack } from 'qwanyx';
import * as Icons from '@mui/icons-material';

export const IconButton = ({ 
  name, 
  size, 
  color, 
  iconName,
  hidden, 
  style, 
  qwantumId,
  script = "",
  targetCardId = "",
  onSave,
  ...props 
}) => {
  const { 
    connectors: { connect, drag },
    isSelected,
    isHovered,
    hasSelectedNode,
    actions: { setProp }
  } = useNode((state) => ({
    isSelected: state.events.selected,
    isHovered: state.events.hovered,
    hasSelectedNode: state.events.selected
  }));

  const { enabled } = useEditor((state) => ({
    enabled: state.options.enabled
  }));

  const useStackSafely = () => {
    try {
      return useStack();
    } catch (error) {
      return null;
    }
  };

  const stackContext = useStackSafely();
  const { text, handleBlur: persistenceBlur } = useTextPersistence(qwantumId, onSave);



  if (hidden) return null;

  const handleClick = (e) => {
    if (!enabled) {
      // In preview mode
      
      // If we have a target card ID and stack context, navigate to that card
      if (targetCardId && stackContext) {
        try {
          stackContext.selectItem(targetCardId, true);
          console.log(`IconButton navigated to card: ${targetCardId}`);
        } catch (error) {
          console.error("Error navigating to card:", error);
        }
      }

      // Also persist the script value if configured
      if (qwantumId && script) {
        const syntheticEvent = {
          target: { value: script }
        };
        persistenceBlur(syntheticEvent);
        if (onSave) {
          onSave(script);
        }
      }
    }
  };

  const getSelectionStyle = () => {
    if (isSelected) {
      return {
        outline: '1px dashed #2196f3',
        outlineOffset: '2px'
      };
    }
    if (isHovered) {
      return {
        outline: '1px dashed #ff9800',
        outlineOffset: '1px'
      };
    }
    return {};
  };

  // Get the icon component
  const IconComponent = Icons[iconName] || Icons.Home;
  
  return (
    <MaterialIconButton 
      ref={ref => connect(drag(ref))} 
      name={name}
      size={size} 
      color={color}
      onClick={handleClick}
      style={{
        transition: 'all 0.2s ease',
        position: 'relative',
        ...style,
        ...getSelectionStyle()
      }}
      {...props}
    >
      <IconComponent />
    </MaterialIconButton>
  );
};

const IconButtonSettings = () => {
  const { actions: { setProp }, props } = useNode((node) => ({
    props: node.data.props
  }));

  // Common Material-UI icons
  const commonIcons = [
    'Home', 'Add', 'Edit', 'Delete', 'Search', 'Settings', 
    'Menu', 'Close', 'Check', 'Clear', 'Save', 'Cancel',
    'ArrowBack', 'ArrowForward', 'ExpandMore', 'ExpandLess',
    'Favorite', 'Star', 'Share', 'Download', 'Upload', 'Print',
    'Email', 'Phone', 'Person', 'Group', 'LocationOn', 'Event'
  ];

  return (
    <div>
      <FormControl fullWidth margin="normal">
        <FormLabel>Component Name</FormLabel>
        <TextField
          value={props.name || ''}
          onChange={(e) => setProp(props => props.name = e.target.value)}
          size="small"
          placeholder="Enter component name"
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Icon Name</FormLabel>
        <TextField
          value={props.iconName || ''}
          onChange={(e) => setProp(props => props.iconName = e.target.value)}
          size="small"
          placeholder="Enter Material-UI icon name"
          helperText="Common icons: Home, Add, Edit, Delete, Search, Settings, Menu, Close"
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Quick Icon Select</FormLabel>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
          {commonIcons.map((icon) => {
            const IconComponent = Icons[icon];
            return (
              <div
                key={icon}
                onClick={() => setProp(props => props.iconName = icon)}
                style={{
                  padding: '8px',
                  border: props.iconName === icon ? '2px solid #2196f3' : '1px solid #ccc',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: props.iconName === icon ? '#e3f2fd' : '#fff'
                }}
              >
                <IconComponent fontSize="small" />
              </div>
            );
          })}
        </div>
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Target Card ID</FormLabel>
        <TextField
          value={props.targetCardId || ''}
          onChange={(e) => setProp(props => props.targetCardId = e.target.value)}
          size="small"
          placeholder="Paste card _id to navigate to"
          helperText="When clicked, button will show this card in detail view"
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Quantum ID</FormLabel>
        <TextField
          value={props.qwantumId || ''}
          onChange={(e) => setProp(props => props.qwantumId = e.target.value)}
          size="small"
          placeholder="Enter quantum ID for persistence"
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Script (Value to Persist)</FormLabel>
        <TextField
          value={props.script || ''}
          onChange={(e) => setProp(props => props.script = e.target.value)}
          size="small"
          multiline
          rows={3}
          placeholder="Enter value to persist on click"
        />
      </FormControl>

      <FormControl size="small" component="fieldset">
        <FormLabel component="legend">Size</FormLabel>
        <RadioGroup 
          defaultValue={props.size} 
          onChange={(e) => setProp(props => props.size = e.target.value)}
        >
          <FormControlLabel label="Small" value="small" control={<Radio size="small" color="primary" />} />
          <FormControlLabel label="Medium" value="medium" control={<Radio size="small" color="primary" />} />
          <FormControlLabel label="Large" value="large" control={<Radio size="small" color="primary" />} />
        </RadioGroup>
      </FormControl>
      
      <FormControl component="fieldset">
        <FormLabel component="legend">Color</FormLabel>
        <RadioGroup 
          defaultValue={props.color} 
          onChange={(e) => setProp(props => props.color = e.target.value)}
        >
          <FormControlLabel label="Default" value="default" control={<Radio size="small" color="default" />} />
          <FormControlLabel label="Primary" value="primary" control={<Radio size="small" color="primary" />} />
          <FormControlLabel label="Secondary" value="secondary" control={<Radio size="small" color="primary" />} />
        </RadioGroup>
      </FormControl>
    </div>
  );
};

IconButton.craft = {
  props: { 
    name: "",
    size: "medium", 
    color: "default",
    iconName: "Home",
    qwantumId: "",
    script: "",
    targetCardId: ""
  },
  related: { 
    settings: IconButtonSettings
  }
};